// @flow
import { ChangeEvent, useContext, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KeyValueEntry } from "../../types/KeyValueEntry";
import { TextFile } from "../../types/TextFile";
import { routes } from "../../routes/routesconfig";
import { ApiContext } from "../../context/KeaAPI/newApi";

type Props = {
  schemaTreeId: number;
};

/**
 * This component allows the user to edit the grading service parameters: key-value pairs and text files.
 * @param props
 * @constructor
 */
export const EditGradingServiceParameters = (props: Props) => {
  type State = {
    keyValues: KeyValueEntry[];
    keyValueEditID: number | undefined;
    activeKV: KeyValueEntry | null;
    // The IDs of the key values that have been deleted. These will be removed from the database when the user clicks "Update".
    // Since all existing kvs have unique ids, and all new kvs have negative ids, which will never be re-issued, we can
    // just store the ids of the deleted kvs.
    deletedKeyValues: number[];
    textFiles: TextFile[];
    textFileEditID: number | undefined;
    // see deletedKeyValues
    deletedTextFiles: number[];
    activeTF: TextFile | null;
    updating: boolean;
    // The next ID to use when creating a new KeyValueItem. Must always be negative to differentiate from existing IDs.
    nextCreatedId: number;
  };

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { gradingServiceApi, keyValueApi, schemaTreeApi, textFileApi } = useContext(ApiContext);

  // get the initial state. can be used to reset
  let initialState: State = {
    keyValues: keyValueApi.elements.filter((kv) => kv.schematreeId === props.schemaTreeId),
    keyValueEditID: undefined,
    deletedKeyValues: [],
    activeKV: null,
    textFiles: textFileApi.elements.filter((tf) => tf.schematreeId === props.schemaTreeId),
    textFileEditID: undefined,
    deletedTextFiles: [],
    activeTF: null,
    updating: false,
    nextCreatedId: -1,
  };
  const [state, setState] = useState<State>(initialState);

  const [dataLoaded, setDataLoaded] = useState({
    keyValue: false,
    textFile: false,
    gradingService: false,
    schemaTree: false,
  });
  useEffect(() => {
    keyValueApi.getAll().then(() => setDataLoaded((prevState) => ({ ...prevState, keyValue: true })));
    textFileApi.getAll().then(() => setDataLoaded((prevState) => ({ ...prevState, textFile: true })));
    gradingServiceApi.getAll().then(() => setDataLoaded((prevState) => ({ ...prevState, gradingService: true })));
    schemaTreeApi.getAll().then(() => setDataLoaded((prevState) => ({ ...prevState, schemaTree: true })));
  }, []);

  // according to copilot, this is the correct way to update the state when the keyValues change, since the setState
  // method in KeyValueContextProvider.updateAll() is asynchronous itself and is not guaranteed to be done when the
  // promise resolves.
  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      keyValues: keyValueApi.elements.filter((kv) => kv.schematreeId === props.schemaTreeId),
      textFiles: textFileApi.elements.filter((tf) => tf.schematreeId === props.schemaTreeId),
    }));
  }, [keyValueApi.elements, textFileApi.elements]);

  // get the tree edge and grading service this page is for
  const treeEdge = schemaTreeApi.elements.find((st) => st.id === props.schemaTreeId);
  const gradingService = gradingServiceApi.elements.find((gs) => gs.id === treeEdge?.childId);

  // Functions for key-value-entries
  const kvHandleDeleteClick = (id: number) => {
    setState((prevState) => ({
      ...prevState,
      deletedKeyValues: [...state.deletedKeyValues, id],
      keyValues: prevState.keyValues.filter((kv) => kv.id !== id),
    }));
  };

  const kvHandleConfirmClick = () => {
    const activeKV = state.activeKV;
    if (!activeKV) {
      console.error("activeKV is null");
      return;
    }

    setState((prevState) => ({
      ...prevState,
      keyValues: prevState.keyValues.map((kv) =>
        kv.id === activeKV.id
          ? {
              ...kv,
              key: activeKV.key,
              value: activeKV.value,
              description: activeKV.description,
            }
          : kv,
      ),
      keyValueEditID: undefined,
    }));
  };

  const kvHandleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const activeKV = state.activeKV; // allow compiler to ensure it is non-null
    if (!activeKV) {
      console.error("activeKV is null");
      return;
    }
    setState({ ...state, activeKV: { ...activeKV, [event.target.name]: event.target.value } });
    console.log(activeKV);
  };

  const kvHandleTextAreaChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const activeKV = state.activeKV; // allow compiler to ensure it is non-null
    if (!activeKV) {
      console.error("activeKV is null");
      return;
    }
    setState({ ...state, activeKV: { ...activeKV, [event.target.name]: event.target.value } });
  };

  const kvHandleCancelClick = () => {
    setState({ ...state, keyValueEditID: undefined, textFileEditID: undefined, activeKV: null });
  };

  const kvHandleEditClick = (id: number) => {
    const activeKV = state.keyValues.find((kv) => kv.id === id) || null;
    setState({ ...state, keyValueEditID: id, activeKV: activeKV });
  };

  const kvHandleAddEntryClick = () => {
    // Create a new KeyValueItem
    const newKeyValue: KeyValueEntry = {
      id: state.nextCreatedId--, // Use the next ID and decrement it. Negative IDs are used for new items.
      key: t("newKeyValueDefaultKey"),
      value: t("newKeyValueDefaultValue"),
      description: t("newKeyValueDefaultDescription"),
      schematreeId: props.schemaTreeId, // Use the schemaTreeId from props
      createdAt: "", // managed by backend
      updatedAt: "", // managed by backend
    };

    // Add the new KeyValueItem to the keyValues array in the state
    setState((prevState) => ({
      ...prevState,
      keyValues: [...prevState.keyValues, newKeyValue],
      lastCreatedID: newKeyValue.id, // Update the lastCreatedID
    }));
  };

  // Functions for text files
  const tfHandleDeleteClick = (id: number) => {
    setState((prevState) => ({
      ...prevState,
      deletedTextFiles: [...state.deletedTextFiles, id],
      textFiles: prevState.textFiles.filter((tf) => tf.id !== id),
    }));
  };

  const tfHandleConfirmClick = () => {
    const activeTF = state.activeTF; // allow compiler to ensure it is non-null
    if (!activeTF) {
      console.error("activeTF is null");
      return;
    }

    setState((prevState) => ({
      ...prevState,
      textFiles: prevState.textFiles.map((tf) =>
        tf.id === activeTF.id ? { ...tf, name: activeTF.name, content: activeTF.content } : tf,
      ),
      textFileEditID: undefined,
    }));
  };

  const tfHandleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const activeTF = state.activeTF; // allow compiler to ensure it is non-null
    if (!activeTF) {
      console.error("activeTF is null");
      return;
    }
    setState({ ...state, activeTF: { ...activeTF, [event.target.name]: event.target.value } });
  };

  const tfHandleCancelClick = () => {
    setState({ ...state, keyValueEditID: undefined, textFileEditID: undefined, activeTF: null });
  };

  const tfHandleEditClick = (id: number) => {
    const activeTF = state.textFiles.find((tf) => tf.id === id) || null;
    setState({ ...state, textFileEditID: id, activeTF: activeTF });
  };

  const tfHandleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      console.error("No file selected");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result?.toString();
      if (!content) {
        console.error("Failed to read file content");
        return;
      }

      const activeTF = state.activeTF; // allow compiler to ensure it is non-null
      if (!activeTF) {
        console.error("activeTF is null");
        return;
      }

      setState({ ...state, activeTF: { ...activeTF, content: content } });
    };
    reader.readAsText(file);
  };

  const exit = () => navigate(routes.schematreeview);

  /**
   * Update all key values and text files.
   * Important: This function leaves the page after updating the key values and text files.
   * The state is stale after the update and the page should not be used anymore!
   */
  const updateAll = async () => {
    if (state.updating) {
      alert(t("updateInProgress"));
      return;
    }

    if (state.keyValues.some((kv) => kv.key === "" || kv.value === "")) {
      alert(t("keyValueEmptyError"));
      return;
    }

    setState({ ...state, updating: true });
    // First update/create the key values and save the promises
    const keyValuePromises = state.keyValues.map(async (kv) => {
      // depending on id create or update
      if (kv.id >= 0) await keyValueApi.update(kv);
      else await keyValueApi.create(kv);
    });

    // delete the key values that have been marked for deletion
    const deleteKeyValuePromises = state.deletedKeyValues.map(async (id) => {
      await keyValueApi.deleteFunction(id);
    });

    // then the text files
    const textFilePromises = state.textFiles.map(async (tf) => {
      // depending on id create or update
      if (tf.id >= 0) await textFileApi.update(tf);
      else await textFileApi.create(tf);
    });

    // delete the text files that have been marked for deletion
    const deleteTextFilePromises = state.deletedTextFiles.map(async (id) => {
      await textFileApi.deleteFunction(id);
    });

    // wait for all promises to finish
    const totalPromise = Promise.all([
      ...keyValuePromises,
      ...textFilePromises,
      ...deleteKeyValuePromises,
      ...deleteTextFilePromises,
    ]).then(() => {
      console.log("All key values and text files updated");
    });

    await totalPromise;
    exit();
  };

  const getKeyValueTable = () => {
    return (
      <>
        <h4 className="title is-4">{t("key-value-pairs")}</h4>
        <div className="table-container">
          <table className="table is-striped is-narrow is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th>{t("key")}</th>
                <th>{t("value")}</th>
                <th>{t("description")}</th>
                <th>{t("actions")}</th>
              </tr>
            </thead>
            <tbody>
              {state.keyValues.map((keyValue) => (
                <tr key={keyValue.id}>
                  <td>
                    {state.keyValueEditID === keyValue.id ? (
                      <input
                        className="input"
                        type="text"
                        name="key"
                        value={state.activeKV?.key ?? ""}
                        onChange={kvHandleInputChange}
                      />
                    ) : (
                      keyValue.key
                    )}
                  </td>
                  <td>
                    {state.keyValueEditID === keyValue.id ? (
                      <textarea
                        className="textarea"
                        name="value"
                        value={state.activeKV?.value ?? ""}
                        onChange={kvHandleTextAreaChange}
                      />
                    ) : (
                      keyValue.value
                    )}
                  </td>
                  <td>
                    {state.keyValueEditID === keyValue.id ? (
                      <textarea
                        className="textarea"
                        name="description"
                        value={state.activeKV?.description ?? ""}
                        onChange={kvHandleTextAreaChange}
                      />
                    ) : (
                      keyValue.description
                    )}
                  </td>
                  <td>
                    {state.keyValueEditID === keyValue.id ? (
                      <>
                        <button className="button is-primary" onClick={() => kvHandleConfirmClick()}>
                          {t("save")}
                        </button>
                        <button className="button" onClick={() => kvHandleCancelClick()}>
                          {t("cancel")}
                        </button>
                      </>
                    ) : (
                      <button className="button" onClick={() => kvHandleEditClick(keyValue.id)}>
                        {t("edit")}
                      </button>
                    )}
                    <button className="button is-danger" onClick={() => kvHandleDeleteClick(keyValue.id)}>
                      {t("delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <button className="button is-normal" onClick={kvHandleAddEntryClick}>
            {t("addKeyValueItem")}
          </button>
        </div>
      </>
    );
  };

  const getTextFileTable = () => {
    return (
      <>
        <h4 className="title is-4">{t("textFiles")}</h4>
        <div className="table-container">
          <table className="table is-striped is-narrow is-hoverable is-fullwidth">
            <thead>
              <tr>
                <th>{t("filename")}</th>
                <th>{t("content")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {state.textFiles.map((textFile) => (
                <tr key={textFile.id}>
                  <td>
                    {state.textFileEditID === textFile.id ? (
                      <input
                        className="input"
                        type="text"
                        name="name"
                        value={state.activeTF?.name ?? ""}
                        onChange={tfHandleInputChange}
                      />
                    ) : (
                      textFile.name
                    )}
                  </td>
                  <td>
                    {state.textFileEditID === textFile.id ? (
                      <input className="input" type="file" accept=".txt" name="content" onChange={tfHandleFileChange} />
                    ) : (
                      <a
                        href={URL.createObjectURL(new Blob([textFile.content], { type: "text/plain" }))}
                        download={textFile.name}
                      >
                        <button className="button">{t("downloadTextFile")}</button>
                      </a>
                    )}
                  </td>
                  <td>
                    {state.textFileEditID === textFile.id ? (
                      <>
                        <button className="button is-primary" onClick={() => tfHandleConfirmClick()}>
                          {t("save")}
                        </button>
                        <button className="button" onClick={() => tfHandleCancelClick()}>
                          {t("cancel")}
                        </button>
                      </>
                    ) : (
                      <button className="button" onClick={() => tfHandleEditClick(textFile.id)}>
                        {t("edit")}
                      </button>
                    )}
                    <button className="button is-danger" onClick={() => tfHandleDeleteClick(textFile.id)}>
                      {t("delete")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/*Now a button that creates a new text file entry*/}
          <button
            className="button is-normal"
            onClick={() => {
              const newTF: TextFile = {
                id: state.nextCreatedId--,
                name: t("newTextFileDefaultName"),
                content: t("newTextFileDefaultContent"),
                schematreeId: props.schemaTreeId,
                createdAt: "",
                updatedAt: "",
              };
              setState((prevState) => ({ ...prevState, textFiles: [...prevState.textFiles, newTF] }));
            }}
          >
            {t("addTextFile")}
          </button>
        </div>
      </>
    );
  };

  const getButtons = () => {
    return (
      <div className="columns mt-4">
        <button className="button is-danger" onClick={() => exit()}>
          {t("cancel")}
        </button>
        <button className="button is-normal is-success" onClick={updateAll}>
          {t("update")}
        </button>
      </div>
    );
  };

  const getTable = () => (
    <div className="card my-2">
      <div className="card-content">
        <div className="content">
          <div className="columns"></div>
          <div className="columns">
            <div className="column has-text-left">
              {Object.values(dataLoaded).some((value) => !value) ? (
                <div>Loading...</div>
              ) : (
                <>
                  {getKeyValueTable()}
                  {getTextFileTable()}
                  {getButtons()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="section">
      <div className="card mt-6">
        <header className="card-header">
          <p className="card-header-title">
            {t("parameterOfGradingservice")
              .replace("{gs}", gradingService?.name ?? "")
              .replace("{id}", treeEdge?.id?.toString() ?? "")}
          </p>
        </header>
        <div className="card-content">
          <div className="content">
            <div className="columns">
              <div className="column has-text-weight-light is-italic">{t("onlyChangeOnUpdatePress")}</div>
            </div>
            {getTable()}
          </div>
        </div>
      </div>
    </div>
  );
};
